// frontend/src/components/client/mobile/MobileProfile.jsx
import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, MapPin, Plane, Bell, MessageCircle, Save, CheckCircle, LogOut } from 'lucide-react';
import { getProfile, getMyAircraft, getNotificationPreferences, updateNotificationPreferences } from '../../../services/clientApi';
import { useClientAuth } from '../../../contexts/ClientAuthContext';

const InfoRow = ({ Icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '0.5px solid #1e293b' }}>
    <div style={{ width: 32, height: 32, borderRadius: 9, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={15} color="#64748b" />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#475569', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: value ? '#e2e8f0' : '#334155' }}>{value || 'Not provided'}</div>
    </div>
  </div>
);

const Section = ({ title, Icon, children }) => (
  <div style={{ background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155', padding: '16px', marginBottom: 14 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <Icon size={15} color="#3b82f6" />
      <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
    </div>
    {children}
  </div>
);

const Toggle = ({ Icon, iconColor, title, subtitle, checked, onChange, disabled }) => (
  <label style={{
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
    borderBottom: '0.5px solid #1e293b', cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.45 : 1,
  }}>
    <div style={{ width: 32, height: 32, borderRadius: 9, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={15} color={iconColor} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>{subtitle}</div>
    </div>
    {/* iOS-style toggle */}
    <div style={{ position: 'relative', width: 44, height: 26, flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
      <div
        onClick={disabled ? undefined : () => onChange({ target: { checked: !checked } })}
        style={{
          width: 44, height: 26, borderRadius: 13,
          background: checked ? '#22c55e' : '#334155',
          transition: 'background 0.2s',
          cursor: disabled ? 'default' : 'pointer',
          position: 'relative',
        }}
      >
        <div style={{
          position: 'absolute', top: 2, left: checked ? 20 : 2,
          width: 22, height: 22, borderRadius: 11, background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          transition: 'left 0.2s',
        }} />
      </div>
    </div>
  </label>
);

const MobileProfile = () => {
  const { client, logout } = useClientAuth();
  const [profile, setProfile] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [prefs, setPrefs] = useState({ email: true, sms: false, telegram: false });

  useEffect(() => {
    Promise.all([getProfile(), getMyAircraft(), getNotificationPreferences()])
      .then(([p, a, n]) => {
        setProfile(p.data);
        setAircraft(a.data);
        if (n.data) setPrefs({
          email: n.data.email_notifications !== false,
          sms: n.data.sms_notifications === true,
          telegram: n.data.telegram_notifications === true,
        });
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const savePrefs = async () => {
    setSaving(true);
    try {
      await updateNotificationPreferences({ email_notifications: prefs.email, sms_notifications: prefs.sms, telegram_notifications: prefs.telegram });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Error saving preferences'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
      <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );

  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`.toUpperCase();

  return (
    <div style={{ padding: '0 16px 100px' }}>
      {/* Avatar block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 0 24px' }}>
        <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg, #1d4ed8, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
          {initials || <User size={28} color="#fff" />}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>{profile?.first_name} {profile?.last_name}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{profile?.company_name || 'Client'}</div>
        </div>
      </div>

      {/* Company */}
      <Section title="Company" Icon={Building}>
        <InfoRow Icon={Building} label="Company" value={profile?.company_name} />
        <InfoRow Icon={Mail} label="Email" value={profile?.email} />
        <InfoRow Icon={User} label="Full name" value={`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()} />
      </Section>

      {/* Contact */}
      <Section title="Contact" Icon={Phone}>
        <InfoRow Icon={Phone} label="Phone" value={profile?.phone} />
        <InfoRow Icon={MapPin} label="Address" value={profile?.address} />
        {profile?.city && <InfoRow Icon={MapPin} label="City / State / Zip" value={`${profile.city} ${profile.state || ''} ${profile.zip_code || ''}`.trim()} />}
      </Section>

      {/* Notifications */}
      <Section title="Notifications" Icon={Bell}>
        <Toggle Icon={Mail} iconColor="#3b82f6" title="Email" subtitle={`Alerts to ${profile?.email || 'your email'}`} checked={prefs.email} onChange={e => setPrefs({ ...prefs, email: e.target.checked })} />
        <Toggle Icon={Phone} iconColor="#22c55e" title="SMS" subtitle={profile?.phone ? `SMS to ${profile.phone}` : 'No phone number registered'} checked={prefs.sms} onChange={e => setPrefs({ ...prefs, sms: e.target.checked })} disabled={!profile?.phone} />
        <Toggle Icon={MessageCircle} iconColor="#38bdf8" title="Telegram" subtitle={profile?.telegram_username ? `@${profile.telegram_username}` : 'No Telegram account'} checked={prefs.telegram} onChange={e => setPrefs({ ...prefs, telegram: e.target.checked })} disabled={!profile?.telegram_username} />
        <div style={{ paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {saved && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#22c55e' }}>
              <CheckCircle size={13} /> Saved!
            </span>
          )}
          <button
            onClick={savePrefs}
            disabled={saving}
            style={{ marginLeft: 'auto', background: '#2563eb', border: 'none', borderRadius: 12, padding: '11px 20px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {saving ? <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Section>

      {/* Aircraft */}
      <Section title={`My aircraft (${aircraft.length})`} Icon={Plane}>
        {aircraft.length === 0 ? (
          <p style={{ fontSize: 13, color: '#475569', paddingTop: 8 }}>No aircraft registered</p>
        ) : aircraft.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '0.5px solid #1e293b' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>{a.tail_number}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{a.manufacturer} {a.model} ({a.year})</div>
            </div>
            <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, background: a.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.15)', color: a.status === 'active' ? '#22c55e' : '#64748b', fontWeight: 600 }}>
              {a.status === 'active' ? 'Active' : a.status}
            </span>
          </div>
        ))}
      </Section>

      {/* Logout */}
      <button
        onClick={logout}
        style={{ width: '100%', background: 'rgba(239,68,68,0.1)', border: '0.5px solid rgba(239,68,68,0.25)', borderRadius: 16, padding: '14px', color: '#ef4444', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        <LogOut size={17} /> Sign out
      </button>

      <p style={{ textAlign: 'center', fontSize: 11, color: '#334155', marginTop: 20 }}>
        To modify your information, please contact your FBO administrator.
      </p>
    </div>
  );
};

export default MobileProfile;